import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SidebarNavigation from '../../components/ui/SidebarNavigation';
import NavigationToggle from '../../components/ui/NavigationToggle';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import UserProfileHeader from '../../components/ui/UserProfileHeader';
import FormHeader from './components/FormHeader';
import HelpSidebar from './components/HelpSidebar';
import RequestForm from './components/RequestForm';
import SuccessModal from './components/SuccessModal';
import { toast } from 'sonner';

const NewRequestForm = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [formProgress, setFormProgress] = useState(1);

  // Mock user data
  const userData = {
    name: 'احمد محمدی',
    plan: 'پلن طلایی',
    credits: 12,
    avatar: null,
  };

  const handleFormSubmit = async (formData) => {
    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Mock successful submission
      const requestData = {
        ...formData,
        id: `REQ-${Date.now()?.toString()?.slice(-6)}`,
        status: 'pending',
        createdAt: new Date()?.toISOString(),
        estimatedCompletion: new Date(
          Date.now() + 48 * 60 * 60 * 1000
        )?.toISOString(),
      };

      setSubmittedData(requestData);
      setShowSuccessModal(true);
      setFormProgress(3);

      toast?.success('درخواست با موفقیت ارسال شد');
    } catch (error) {
      toast?.error('خطا در ارسال درخواست. لطفاً دوباره تلاش کنید');
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = (formData) => {
    // Save to localStorage as draft
    const draftData = {
      ...formData,
      savedAt: new Date()?.toISOString(),
      type: 'draft',
    };

    localStorage.setItem('requestDraft', JSON.stringify(draftData));
    setFormProgress(2);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    navigate('/login');
    toast?.success('با موفقیت خارج شدید');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Toggle for Mobile */}
      <NavigationToggle isOpen={sidebarOpen} onToggle={toggleSidebar} />

      {/* Sidebar Navigation */}
      <SidebarNavigation isCollapsed={!sidebarOpen} onToggle={toggleSidebar} />

      {/* Main Content */}
      <div
        className={`transition-all duration-300 ease-in-out ${
          sidebarOpen ? 'lg:mr-80' : 'lg:mr-80'
        }`}>
        {/* Top Header */}
        <header className="bg-card border-b border-border px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 space-x-reverse">
              <h1 className="text-xl font-semibold text-foreground lg:hidden">
                درخواست جدید
              </h1>
            </div>

            <UserProfileHeader user={userData} onLogout={handleLogout} />
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          {/* Breadcrumb */}
          <BreadcrumbTrail className="mb-6" />

          {/* Form Header */}
          <FormHeader
            currentStep={formProgress}
            totalSteps={3}
            estimatedTime="۵-۱۰ دقیقه"
          />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            {/* Form Section */}
            <div className="xl:col-span-3">
              <RequestForm
                onSubmit={handleFormSubmit}
                onSaveDraft={handleSaveDraft}
                isSubmitting={isSubmitting}
              />
            </div>

            {/* Help Sidebar */}
            <div className="xl:col-span-1">
              <HelpSidebar />
            </div>
          </div>
        </main>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        requestData={submittedData}
      />

      {/* Mobile Sidebar Backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default NewRequestForm;
